/**
 * PDF Source Viewer Component
 *
 * Side-by-side PDF viewer for extraction review.
 * Shows the source PDF next to extracted data with page navigation.
 *
 * Features:
 * - Displays PDF from GCS via signed URL
 * - Navigates to specific pages
 * - Shows which pages contain rate tables
 * - Confidence indicator for extracted fields
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    OpenInNew as OpenInNewIcon,
    Warning as WarningIcon,
    CheckCircle as ConfidentIcon,
    Help as UncertainIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import { useAuth } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

/**
 * Confidence indicator component
 *
 * Shows confidence level (0-100) with color coding:
 * - Green (80-100): High confidence
 * - Yellow (60-79): Medium confidence, may need review
 * - Red (0-59): Low confidence, needs human verification
 */
export const ConfidenceIndicator = ({ confidence, label, compact = false }) => {
    if (confidence === undefined || confidence === null) return null;

    const getColor = (conf) => {
        if (conf >= 80) return '#2ecc71'; // Green
        if (conf >= 60) return '#f39c12'; // Yellow/Orange
        return '#e74c3c'; // Red
    };

    const getIcon = (conf) => {
        if (conf >= 80) return <ConfidentIcon sx={{ fontSize: 14 }} />;
        if (conf >= 60) return <UncertainIcon sx={{ fontSize: 14 }} />;
        return <WarningIcon sx={{ fontSize: 14 }} />;
    };

    const color = getColor(confidence);

    if (compact) {
        return (
            <Tooltip title={`${label}: ${confidence}% confidence`}>
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color,
                        ml: 0.5,
                    }}
                >
                    {getIcon(confidence)}
                </Box>
            </Tooltip>
        );
    }

    return (
        <Chip
            size="small"
            icon={getIcon(confidence)}
            label={`${confidence}%`}
            sx={{
                bgcolor: alpha(color, 0.15),
                color,
                borderColor: color,
                border: '1px solid',
                '& .MuiChip-icon': { color },
            }}
        />
    );
};

/**
 * PDF Source Viewer
 *
 * Displays PDF in an iframe with page navigation and rate table page indicators.
 */
const PDFSourceViewer = ({
    pdfGcsUri,           // GCS URI of the PDF (gs://bucket/path)
    pageCount = 0,       // Total pages in PDF
    pagesWithRates = [], // Pages that contain rate tables
    currentPage = 1,     // Current page to display
    onPageChange,        // Callback when page changes
    height = 600,        // Height of the viewer
}) => {
    const { currentUser } = useAuth();
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [localPage, setLocalPage] = useState(currentPage);

    // Fetch signed URL for the PDF
    useEffect(() => {
        const fetchPdfUrl = async () => {
            if (!pdfGcsUri || !currentUser) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/api/pdf-url`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ pdf_uri: pdfGcsUri }),
                });

                if (!res.ok) {
                    throw new Error('Failed to get PDF URL');
                }

                const data = await res.json();
                setPdfUrl(data.url);
            } catch (err) {
                console.error('PDF URL fetch error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPdfUrl();
    }, [pdfGcsUri, currentUser]);

    // Sync local page with prop
    useEffect(() => {
        setLocalPage(currentPage);
    }, [currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > pageCount) return;
        setLocalPage(newPage);
        onPageChange?.(newPage);
    };

    const goToPrevPage = () => handlePageChange(localPage - 1);
    const goToNextPage = () => handlePageChange(localPage + 1);

    // Check if current page has rate tables
    const isRatePage = pagesWithRates.includes(localPage);

    if (loading) {
        return (
            <Paper sx={{ p: 3, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={40} />
                <Typography sx={{ ml: 2, color: 'text.secondary' }}>Loading PDF...</Typography>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper sx={{ p: 3, height }}>
                <Alert severity="error">
                    Failed to load PDF: {error}
                </Alert>
            </Paper>
        );
    }

    if (!pdfGcsUri) {
        return (
            <Paper sx={{ p: 3, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">
                    No PDF available for this extraction
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper
            sx={{
                height,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            {/* Header with navigation */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                }}
            >
                {/* Page navigation */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={goToPrevPage}
                        disabled={localPage <= 1}
                    >
                        <PrevIcon />
                    </IconButton>

                    <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>
                        Page {localPage} of {pageCount}
                    </Typography>

                    <IconButton
                        size="small"
                        onClick={goToNextPage}
                        disabled={localPage >= pageCount}
                    >
                        <NextIcon />
                    </IconButton>

                    {isRatePage && (
                        <Chip
                            size="small"
                            label="Rate Table"
                            sx={{
                                bgcolor: alpha(vortxColors.gold, 0.2),
                                color: vortxColors.gold,
                                fontWeight: 500,
                            }}
                        />
                    )}
                </Box>

                {/* Rate pages quick nav */}
                {pagesWithRates.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                            Rate pages:
                        </Typography>
                        {pagesWithRates.map((page) => (
                            <Chip
                                key={page}
                                label={page}
                                size="small"
                                onClick={() => handlePageChange(page)}
                                sx={{
                                    cursor: 'pointer',
                                    bgcolor: page === localPage
                                        ? vortxColors.teal
                                        : alpha(vortxColors.teal, 0.1),
                                    color: page === localPage ? 'white' : vortxColors.teal,
                                    '&:hover': {
                                        bgcolor: page === localPage
                                            ? vortxColors.teal
                                            : alpha(vortxColors.teal, 0.2),
                                    },
                                }}
                            />
                        ))}
                    </Box>
                )}

                {/* Actions */}
                <Box>
                    <Tooltip title="Open in new tab">
                        <IconButton
                            size="small"
                            onClick={() => window.open(pdfUrl, '_blank')}
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* PDF iframe */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {pdfUrl && (
                    <iframe
                        src={`${pdfUrl}#page=${localPage}&view=FitH&pagemode=none&navpanes=0`}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                        }}
                        title="PDF Source Document"
                    />
                )}
            </Box>
        </Paper>
    );
};

export default PDFSourceViewer;
